import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Copy, Share2, Users } from 'lucide-react';
import { Avatar, Button, Card, EmptyState, SectionTitle, Skeleton } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { shareLink } from '../../lib/share';

interface Invite {
  code: string;
  url: string;
  customers: number;
}
interface MyCustomer {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  photoUrl: string | null;
  rides: number;
  since: string;
}

/** Enlace personal del conductor: los clientes que se registran con él son solo suyos. */
export function InvitePage() {
  const toast = useToast();
  const invite = useQuery({ queryKey: ['driver-invite'], queryFn: () => api<Invite>('/drivers/me/invite') });
  const customers = useQuery({ queryKey: ['driver-customers'], queryFn: () => api<MyCustomer[]>('/drivers/me/customers') });
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    if (!invite.data) return;
    void QRCode.toDataURL(invite.data.url, { width: 520, margin: 1, color: { dark: '#111111', light: '#FFFFFF' } }).then(setQr);
  }, [invite.data]);

  const share = async () => {
    if (!invite.data) return;
    try {
      const r = await shareLink(invite.data.url, 'Mi servicio de transporte', 'Pide tus viajes conmigo desde aquí:');
      if (r === 'copied') toast('Enlace copiado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };
  const copy = async () => {
    if (!invite.data) return;
    await navigator.clipboard.writeText(invite.data.url).catch(() => undefined);
    toast('Enlace copiado', 'success');
  };

  return (
    <Page title="Mi enlace" back="/driver/more" onRefresh={() => Promise.all([invite.refetch(), customers.refetch()])}>
      <div className="flex flex-col gap-4">
        <Card className="p-5 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-[var(--text-muted)]">
            Comparte este enlace o deja que tus clientes escaneen el código. Quienes se registren con él serán <strong className="text-[var(--text)]">tus clientes</strong>: sus solicitudes solo te llegan a ti.
          </p>
          {invite.isLoading || !qr ? <Skeleton className="size-56" /> : <img src={qr} alt="Código QR de tu enlace" className="size-56 rounded-xl border border-[var(--border)]" />}
          {invite.data && <p className="font-mono text-sm break-all">{invite.data.url.replace(/^https?:\/\//, '')}</p>}
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button variant="secondary" icon={<Copy className="size-5" />} onClick={copy} disabled={!invite.data}>Copiar</Button>
            <Button icon={<Share2 className="size-5" />} onClick={share} disabled={!invite.data}>Compartir</Button>
          </div>
        </Card>

        <section>
          <SectionTitle title={`Mis clientes${customers.data ? ` (${customers.data.length})` : ''}`} />
          {customers.isLoading && <Skeleton className="h-24" />}
          {customers.data?.length === 0 && <EmptyState icon={<Users className="size-6" />} title="Aún no tienes clientes" description="Cuando alguien se registre con tu enlace aparecerá aquí." />}
          {!!customers.data?.length && (
            <Card className="divide-y divide-[var(--border)]">
              {customers.data.map((c) => (
                <div key={c.id} className="p-4 flex items-center gap-3">
                  <Avatar src={c.photoUrl} firstName={c.firstName} lastName={c.lastName} size={40} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{c.firstName} {c.lastName}</p>
                    <p className="text-xs text-[var(--text-muted)]">{c.rides} viaje(s){c.phone ? ` · ${c.phone}` : ''}</p>
                  </div>
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="tap text-sm font-semibold text-brand-red">Llamar</a>
                  )}
                </div>
              ))}
            </Card>
          )}
        </section>
      </div>
    </Page>
  );
}
